import envVar from '../config/envVars';
import { RolloverDashboardReport } from '../modules/RolloverDashboard/types';
import { StaffAttendance } from '../modules/StaffAttendanceReport/types/StaffAttendanceTypes';
import {
  DayWiseFeeBookReportWithPagination,
  pendingFeeSummaryFilter,
  RolloverStatusReportParams
} from '../types/ReportType';
import authenticationUtils from '../utils/authenticationUtils';
import httpClient from './httpClient';
import httpGet from './httpClient/httpGet';
import httpPost from './httpClient/httpPost';

export interface RolloverDashboardReportFilter {
  academicYear: string;
  getGradeWiseSummary?: boolean;
}

const fetchDayWiseFeeBookReportApi = ({
  fromDate,
  toDate,
  academicYear,
  grade,
  division,
  perPage,
  pageNo
}: DayWiseFeeBookReportWithPagination) => {
  const encodedTo = encodeURIComponent(`${toDate}`);
  const query = `fromDate=${fromDate}&toDate=${encodedTo}&session=${academicYear}&grade=${grade}&division=${division}&perPage=${perPage}&page=${pageNo}`;
  const url = `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/reports/dayWiseFeeBook?${query}`;
  return httpGet(url, {
    headers: {
      Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
    }
  });
};

const fetchPendingFeeSummaryReport = ({
  date,
  academicYear,
  grade,
  division
}: pendingFeeSummaryFilter) => {
  const query = `asOnDate=${date}&session=${academicYear}&grade=${grade}&division=${division}`;
  const url = `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/reports/pendingPaymentsSummary?${query}`;
  return httpGet(url, {
    headers: {
      Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
    }
  });
};

export interface OutstandingFeeReportFilter {
  date: string;
  academicYear: string;
  pageNo: number;
  perPage: number;
}

const fetchOutstandingFeeReport = ({
  date,
  academicYear,
  pageNo,
  perPage
}: OutstandingFeeReportFilter) => {
  const query = `asOnDate=${date}&session=${academicYear}&perPage=${perPage}&page=${pageNo}`;
  const url = `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/reports/outstanding-fees?${query}`;
  return httpGet(url, {
    headers: {
      Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
    }
  });
};

export interface AttendanceTrackingReportFilter {
  school: string;
  grade: string;
  division: string;
  startDate: string;
  endDate: string;
}

const fetchAttendanceTrackingReport = ({
  school,
  grade,
  division,
  startDate,
  endDate
}: AttendanceTrackingReportFilter) => {
  const query = `attendance/schools/${school}/grade/${grade}/division/${division}?fromDate=${startDate}&toDate=${endDate}`;
  const url = `${envVar.REACT_APP_BASE_API_URL}/${query}`;
  return httpGet(url);
};

export interface StaffAttendanceReportFilter {
  school: string;
  fromDate: string;
  toDate: string;
  role?: string;
  perPage?: number;
  page?: number;
}

const fetchStaffAttendanceReport = async ({
  school,
  fromDate,
  toDate,
  role,
  perPage,
  page
}: StaffAttendanceReportFilter) => {
  const base = `${envVar.REACT_APP_BASE_API_URL}/reports/staff-attendance/schools/${school}`;
  const queryParts: string[] = [`fromDate=${fromDate}`, `toDate=${toDate}`];
  if (role) queryParts.push(`role=${role}`);
  if (perPage) queryParts.push(`perPage=${perPage}`);
  if (page) queryParts.push(`page=${page}`);
  const url = `${base}?${queryParts.join('&')}`;
  return httpGet(url) as unknown as {
    data: StaffAttendance[];
    hasMore: boolean;
  };
};

const fetchCancelledPaymentsLog = async (
  academicYear: string,
  grade: string
) => {
  const query = `academicYear=${academicYear}&grade=${grade}`;
  const url = `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/reports/cancelled-payments?${query}`;
  return httpGet(url, {
    headers: {
      Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
    }
  });
};

export interface ChequeDetailReportFilter {
  chequeStatus: string;
  collectionType: string;
  grade: string;
  academicYear: string;
}

export interface ChequeDetailReportWithPagination extends ChequeDetailReportFilter {
  page: number;
  perPage: number;
}

const fetchChequeDetailReport = async ({
  chequeStatus,
  collectionType,
  academicYear,
  grade,
  page,
  perPage
}: ChequeDetailReportWithPagination) => {
  const query = `chequeStatus=${chequeStatus}&collectionType=${collectionType}&grade=${grade}&page=${page}&perPage=${perPage}&session=${academicYear}`;
  const url = `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/reports/chequeDetail?${query}`;
  const response = await httpGet(url, {
    headers: {
      Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
    }
  });
  return response;
};
export interface fetchStudentLCReportFilter {
  school: string;
  fromDate: string;
  toDate: string;
}

const fetchStudentLCReport = async ({
  school,
  fromDate,
  toDate
}: fetchStudentLCReportFilter) =>
  httpGet(
    `${envVar.REACT_APP_BASE_API_URL}/schools/${school}/report/lc-students?requestedFrom=${fromDate}&requestedTo=${toDate}`
  );

const fetchRolloverStatusReport = ({
  academicYear,
  grade,
  perPage,
  pageNo
}: RolloverStatusReportParams) => {
  const params: string[] = [
    `academicYear=${academicYear}`,
    `perPage=${perPage}`,
    `page=${pageNo}`
  ];
  if (grade) params.push(`grade=${grade}`);
  const query = params.join('&');
  const url = `${envVar.REACT_APP_BASE_API_URL}/reports/rollover-status?${query}`;
  return httpGet(url, {});
};

const fetchRolloverDashboardReport = async ({
  academicYear,
  getGradeWiseSummary = false
}: RolloverDashboardReportFilter) => {
  const url = `${envVar.REACT_APP_BASE_API_URL}/reports/rollover-dashboard?academicYear=${academicYear}&getGradeWiseSummary=${getGradeWiseSummary}`;
  const response = await httpClient.get(url);
  return response as unknown as RolloverDashboardReport[];
};

export interface StudentStopsReportRequestBody {
  studentName?: string;
  grades?: string[];
  divisions?: string[];
}

const fetchStudentTransportReport = async (
  school: string,
  studentTransportReportFilter: StudentStopsReportRequestBody
) => {
  const url = `${envVar.REACT_APP_BASE_API_URL}/reports/schools/${school}/student-stops`;
  const response = await httpPost(url, studentTransportReportFilter);
  return response;
};

const report = {
  fetchDayWiseFeeBookReportApi,
  fetchPendingFeeSummaryReport,
  fetchOutstandingFeeReport,
  fetchChequeDetailReport,
  fetchAttendanceTrackingReport,
  fetchCancelledPaymentsLog,
  fetchStudentLCReport,
  fetchRolloverStatusReport,
  fetchRolloverDashboardReport,
  fetchStudentTransportReport,
  fetchStaffAttendanceReport
};

export default report;
